import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { loginProfissional } from '@/api/profissionalApi'
import { TenantIdentityCard } from '@/components/TenantIdentity'
import {
  getClientPublicEntryPath,
  getTenantEntryPath,
  getTenantSlug,
  hasTenantBound,
  setTenantSlug,
} from '@/config/tenant'
import { useTenant } from '@/contexts/TenantContext'
import { setProfissional } from '@/utils/profissionalAuth'
import { consumeAuthNotice } from '@/utils/authFlow'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Profissional.module.css'

export function ProfissionalLoginPage() {
  const navigate = useNavigate()
  const { nome: empresaNome } = useTenant()
  const tenantLocked = hasTenantBound()
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [tenantSlug, setTenantSlugField] = useState(() => getTenantSlug())
  const [erro, setErro] = useState('')
  const [loading, setLoading] = useState(false)
  const { notifySaved, notifyError, notifyValidation } = useSaveFeedback()

  useEffect(() => {
    const msg = consumeAuthNotice('profissional')
    if (msg) setErro(msg)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')
    if (!email.trim() || !senha.trim()) {
      const msg = 'Preencha e-mail e senha.'
      setErro(msg)
      notifyValidation(msg)
      return
    }
    const slugTrim = (tenantLocked ? getTenantSlug() : tenantSlug).trim().toLowerCase()
    if (!slugTrim) {
      const msg = 'Abra primeiro o link da sua empresa (formato /e/nome-da-empresa).'
      setErro(msg)
      notifyValidation(msg)
      return
    }
    setTenantSlug(slugTrim)
    setLoading(true)
    try {
      const data = await loginProfissional(email.trim(), senha)
      setProfissional(null, data.profissional)
      notifySaved('Sessão iniciada. Bem-vindo ao painel.')
      navigate('/profissional/painel', { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao iniciar sessão.'
      setErro(msg)
      notifyError(err, msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className={`standalone-page ${styles.page}`}>
      <div className={styles.card}>
        <div className={styles.loginMark} aria-hidden>
          P
        </div>
        <p className={styles.kicker}>Área da equipa</p>
        <h1 className={styles.title}>
          {tenantLocked ? empresaNome : 'Acesso profissional'}
        </h1>
        <p className={styles.subtitle}>
          {tenantLocked
            ? 'Entre com o e-mail e a senha da equipa desta empresa.'
            : 'Use o link oficial da empresa ou o identificador abaixo.'}
        </p>
        {erro && <p className={styles.error}>{erro}</p>}
        <form className={styles.form} onSubmit={handleSubmit}>
          {!tenantLocked && (
            <>
              <label className={styles.label} htmlFor="prof-tenant">
                Identificador da empresa
              </label>
              <input
                id="prof-tenant"
                className={styles.input}
                type="text"
                value={tenantSlug}
                onChange={(e) => setTenantSlugField(e.target.value)}
                onBlur={() => {
                  const s = tenantSlug.trim().toLowerCase()
                  if (s) setTenantSlug(s)
                }}
                placeholder="ex.: meu-salao"
                autoComplete="off"
                spellCheck={false}
              />
            </>
          )}
          {tenantLocked && <TenantIdentityCard compact />}
          <label className={styles.label} htmlFor="prof-email">E-mail</label>
          <input
            id="prof-email"
            className={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="profissional@salao.com"
            autoComplete="username"
          />
          <label className={styles.label} htmlFor="prof-senha">Senha</label>
          <input
            id="prof-senha"
            className={styles.input}
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
          />
          <button className={styles.primaryBtn} type="submit" disabled={loading}>
            {loading ? 'A entrar…' : 'Entrar'}
          </button>
        </form>
        {tenantLocked && (
          <Link to={getTenantEntryPath(getTenantSlug())} className={styles.backBtn}>
            ← Portal da empresa
          </Link>
        )}
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(tenantLocked ? getClientPublicEntryPath() : '/')}
        >
          App do cliente
        </button>
      </div>
    </main>
  )
}

